import React, { useEffect, useRef, useState } from "react";
import CommonTable from "components/ReactTable/Table";

import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Label,
  FormGroup,
  Form,
  Input,
  Row,
  Col,
  Container,
  CardFooter,
  Badge,
  UncontrolledTooltip,
} from "reactstrap";
import RightPanelCard from "components/Common/RightPanelCard";

import {
  fixedWidthCustomStyle,
  MASTER_STATUS_OPTIONS,
  DEFAULT_RECORDS_PER_PAGE,
} from "components/Common/constants";
import Select, { components } from "react-select";
import { showAlert } from "components/Common/commonFunctions";

import NotificationAlert from "react-notification-alert";
import { useDispatch, useSelector } from "react-redux";
import { getJobTypeList, addJobType, editJobType } from "actions/jobType";
import { jobTypeList } from "constants/jobType";
import { WORK_ORDER, SMALL_CASE_WORK_ORDER } from "components/Common/constants";
import {} from "components/Common/constants";

const initialFields = {
  jobTypeName: { value: "", error: "" },
  status: { value: "ACTIVE", error: "" },
};

const JobTypeMaster = () => {
  const dispatch = useDispatch();

  const notificationAlertRef = useRef(null);

  const jobTypeState = useSelector((state) => state.jobType.jobTypeList);

  const [jobTypeListState, setJobTypeListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: "add", // it can be "add" or "edit"
  });

  const [editingUser, setEditingUser] = useState(null);
  const [pageNum, setPageNum] = useState(1);

  const [filterState, setFilterState] = useState({
    searchName: "",
    searchStatus: null,
    filterParams: [],
  });

  const [formData, setFormData] = useState(initialFields);

  const [showLoader, setShowLoader] = useState(false);

  const [showLoaderOnStatus, setShowLoaderOnStatus] = useState({}); // id -> bool

  useEffect(() => {
    getJobTypeList(dispatch, {
      pageNum: pageNum || 1,
      filters: buildFilterParams(),
    });
    return () => {
      dispatch({ type: jobTypeList, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  // React to redux jobTypeState changes

useEffect(() => {
  // Wait until API response is available
  if (!jobTypeState?.data && !jobTypeState?.code) return;
  const items = jobTypeState?.data?.jobType || [];

  if (
    jobTypeState?.code &&
    jobTypeState?.code !== 200 &&
    jobTypeState?.message
  ) {
    setJobTypeListState({
      isLoading: false,
      error:
        jobTypeState.message ||
        `Failed to load ${SMALL_CASE_WORK_ORDER} type list! Please try again.`,
      noRecords: false,
      showLoaderOnClick: false,
    });
  } else {
    setJobTypeListState({
      isLoading: false,
      error: "",
      noRecords: items.length === 0,
      showLoaderOnClick: false,
    });
  }
}, [jobTypeState]);

  const buildFilterParams = () => {
    const filterParams = [];
    if (filterState.searchName) {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filterState.searchName],
      });
    }
    if (filterState.searchStatus) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filterState.searchStatus.value],
      });
    }
    return JSON.stringify(filterParams);
  };

  // Utility: notification
  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  // Prepare table data defensively

  const formattedData =
    jobTypeState?.data?.jobType?.map((item, index) => {
      return {
        ...item,
        srNo:
          (pageNum - 1) *
            (jobTypeState?.data?.noOfRecordsPerPage ||
              DEFAULT_RECORDS_PER_PAGE) +
          index +
          1,
      };
    }) ?? [];

  const toggleModal = (mode = "add", user = null) => {
    if (modalState.isOpen) {
      resetForm();
      setEditingUser(null);
      setModalState({ isOpen: false, mode: "add" });
    } else {
      if (mode === "edit" && user) {
        setEditingUser(user);
        setFormData({
          jobTypeName: { value: user.name || "", error: "" },
          status: {
            value: user.status === "ACTIVE" ? "ACTIVE" : "INACTIVE",
            error: "",
          },
        });
      } else {
        resetForm();
      }
      setModalState({ isOpen: true, mode });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value,
        error: "",
      },
    }));
  };

  const handleSearch = () => {
    const newfilterParams = buildFilterParams();
    if (JSON.stringify(filterState.filterParams) !== newfilterParams) {
      setJobTypeListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setFilterState((prev) => ({
        ...prev,
        filterParams: newfilterParams,
      }));
      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleClear = () => {
    setJobTypeListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setFilterState({
      searchName: "",
      searchStatus: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const resetForm = () => {
    setFormData(initialFields);
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "jobTypeName":
        return !value ? `Please enter ${SMALL_CASE_WORK_ORDER} type name` : "";
      default:
        return "";
    }
  };

  const validateForm = () => {
    // build new state with errors
    const newForm = { ...formData };
    Object.keys(newForm).forEach((key) => {
      const val = newForm[key]?.value ?? newForm[key]; // defensive
      const error = getValidationError(
        key,
        typeof val === "string" ? val.trim() : val,
      );
      newForm[key] = {
        ...newForm[key],
        error,
      };
    });

    setFormData(newForm);

    // return true only if no errors
    return Object.values(newForm).every((f) =>
      f.error ? f.error === "" : true,
    );
  };

  const handleOnBlur = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmed);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmed,
        error,
      },
    }));
  };

  // Add submit -> call addJobType API
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return; // will set inline errors

    try {
      setShowLoader(true);
      const bodyData = {
        name: formData.jobTypeName.value.trim(),
        status: formData.status.value,
      };
      const res = await addJobType(bodyData);
      if (res?.code === 200) {
        notify(
          "success",
          res.message || `${WORK_ORDER} Type added successfully`,
        );
        toggleModal();
        if (pageNum === 1)
          getJobTypeList(dispatch, pageNum, filterState.filterParams);
        else setPageNum(1);
      } else {
        notify("danger", res?.message || `Failed to add ${WORK_ORDER} Type!`);
      }
    } catch (err) {
      notify("danger", `Failed to add ${WORK_ORDER} Type!`);
    } finally {
      setShowLoader(false);
    }
  };

  // Edit submit -> call editJobType API
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    if (!validateForm()) return;

    try {
      setShowLoader(true);
      const bodyData = {
        name: formData.jobTypeName.value.trim(),
        status: formData.status.value,
      };
      const res = await editJobType(editingUser.ID, bodyData);
      if (res?.code === 200) {
        notify(
          "success",
          res.message || `${WORK_ORDER} Type updated successfully`,
        );
        toggleModal();
        getJobTypeList(dispatch, pageNum, filterState.filterParams);
      } else {
        notify("danger", res?.message || `Failed to update ${WORK_ORDER} Type`);
      }
    } catch (err) {
      notify("danger", `Failed to update ${WORK_ORDER} Type`);
    } finally {
      setShowLoader(false);
    }
  };

  // it call editJobType API with new status to cange old one
  const handleStatusToggle = async (data) => {
    const isActive = data.status === "ACTIVE" || data.status === "ACTIVE";
    showAlert({
      title: `Are you sure you want to ${
        isActive ? "inactivate" : "activate"
      } this ${SMALL_CASE_WORK_ORDER} type?`,
      message: `${WORK_ORDER} Type "${data.name}" will become ${
        isActive ? "inactive" : "active"
      }.`,
      type: "warning",
      confirmStyle: "default",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          setShowLoaderOnStatus((prev) => ({ ...prev, [data.ID]: true }));

          const newStatus = isActive ? "INACTIVE" : "ACTIVE";

          const res = await editJobType(data.ID, { status: newStatus });

          if (res?.code === 200) {
            notify(
              "success",
              res.message || `${WORK_ORDER} Type status updated`,
            );
            // Refresh list
            getJobTypeList(dispatch, pageNum, filterState.filterParams);
          } else {
            const message = Array.isArray(res?.message)
              ? res?.message[0]?.Msg
              : res?.message || "Failed to update status!";
            notify("danger", message);
          }
        } catch (e) {
          notify("danger", "Failed to update status!");
        } finally {
          setShowLoaderOnStatus((prev) => ({ ...prev, [data.ID]: false }));
        }
      },
      onCancel: () => {},
    });
  };

  // Table columns
  const columns = [
  { Header: "Sr. no.", accessor: "srNo", className: "col-srNo" },
  {
    Header: `${WORK_ORDER} Type Name`,
    accessor: "name",
    className: "col-jt-name", // added
  },
  {
    Header: "Status",
    accessor: "status",
    className: "col-jt-status", // added
    Cell: ({ value }) => (
      <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
        {value}
      </Badge>
    ),
  },
  {
    Header: "Actions",
    accessor: "actions",
    className: "col-jt-actions", // changed from "text-center"
    disableSortBy: true,
    Cell: ({ row }) => (
      <div className="actions-right d-flex justify-content-start">
        <Button
          onClick={() => toggleModal("edit", row.original)}
          color="link"
          size="sm"
          className="btn-icon btn-neutral ml-1 icon-info"
          id={`edit${row.original.ID}`}
        >
          <i className="tim-icons icon-pencil tim-md" />
        </Button>

        <UncontrolledTooltip delay={0} target={`edit${row.original.ID}`}>
          Edit
        </UncontrolledTooltip>
        <Button
          onClick={() => handleStatusToggle(row.original)}
          color="link"
          size="sm"
          className="btn-icon btn-neutral ml-1"
          id={`status${row.original.ID}`}
          disabled={!!showLoaderOnStatus[row.original.ID]}
        >
          {showLoaderOnStatus[row.original.ID] ? (
            <i className="fa fa-spinner fa-spin" />
          ) : row.original.status === "ACTIVE" ? (
            <i className="fa fa-xmark custom-icon btn-icon icon-danger" />
          ) : (
            <i className="fa fa-check custom-icon btn-icon icon-success" />
          )}
        </Button>
        <UncontrolledTooltip delay={0} target={`status${row.original.ID}`}>
          {row.original.status === "ACTIVE" ? "Inactivate" : "Activate"}{" "}
          {WORK_ORDER}
          Type
        </UncontrolledTooltip>
      </div>
    ),
  },
];

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>

      <Container fluid className="content mt-4">
        <Row>
          <Col md="12">
            <Card className="card-user">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <CardTitle tag="h3" className="font-weight-normal">
                  {WORK_ORDER} Types
                </CardTitle>{" "}
                <Button
                  color="default"
                  onClick={() => toggleModal("add")}
                  className="btn-round btn-style"
                >
                  <i className="fa fa-plus mr-1" />
                  {WORK_ORDER} Type
                </Button>
              </CardHeader>

              <CardBody>
                <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder={`Search by ${SMALL_CASE_WORK_ORDER} type`}
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={150}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={MASTER_STATUS_OPTIONS}
                      placeholder="Status"
                      value={filterState.searchStatus}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchStatus: selected,
                        }))
                      }
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
                      onClick={handleSearch}
                    >
                      {jobTypeListState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin search-loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>
                    {filterState.filterParams.length > 0 && (
                      <Button
                        color="default"
                        className="mb-3 filter-btn btn-simple mt-2"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {jobTypeState?.data?.totalRecords ? (
                    filterState.filterParams &&
                    filterState.filterParams.length > 0 ? (
                      <span className="font-weight-bold">
                        Showing {jobTypeState?.data?.totalFilteredRecords} of{" "}
                        {jobTypeState?.data?.totalRecords} {WORK_ORDER} Types
                      </span>
                    ) : (
                      <span className="font-weight-bold">
                        Total {WORK_ORDER} Types:{" "}
                        {jobTypeState?.data?.totalRecords}
                      </span>
                    )
                  ) : null}
                </div>

                <div className="table-responsive">
                  {jobTypeListState.isLoading ? (
                    <div className="loaderContainer">
                      <i className="fa fa-spinner fa-spin fa-2xl loader" />
                    </div>
                  ) : jobTypeListState.error ? (
                    <div className="loaderContainer">
                      {jobTypeListState.error}
                    </div>
                  ) : jobTypeListState.noRecords ? (
                    <div className="loaderContainer text-muted">
                      No {SMALL_CASE_WORK_ORDER} types added yet.
                    </div>
                  ) : (
                    <div className="table-responsive job-type-table">
                    <CommonTable
                      showPaginationBottom={true}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={pageNum}
                      data={formattedData}
                      resizable={false}
                      columns={columns}
                      className="-striped -highlight table-hover"
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        (jobTypeState?.data?.totalFilteredRecords || 0) /
                          (jobTypeState?.data?.noOfRecordsPerPage ||
                            DEFAULT_RECORDS_PER_PAGE),
                      )}
                    />
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Modal for Add and Edit Job Type*/}
        <RightPanelCard
          isOpen={modalState.isOpen}
          toggle={() => toggleModal()}
          className="modal-lg"
        >
          <Card className="mb-0">
            <CardHeader className="d-flex align-items-center justify-content-between mb-2">
              {modalState.mode === "add"
                ? `Add ${WORK_ORDER} Type`
                : `Edit ${WORK_ORDER} Type`}
              <i
                className="fa fa-close text-muted pointer"
                onClick={() => toggleModal()}
              />
            </CardHeader>

            <CardBody>
              <Row>
                <Col md="6">
                  <FormGroup
                    className={`has-label ${
                      formData.jobTypeName.error ? "has-danger" : ""
                    } d-flex flex-column align-items-start`}
                  >
                    <Label>
                      {WORK_ORDER} Type Name{" "}
                      <span className="validation-error">*</span>
                    </Label>
                    <Input
                      name="jobTypeName"
                      type="text"
                      placeholder={`Enter ${SMALL_CASE_WORK_ORDER} type name`}
                      autoComplete="off"
                      value={formData.jobTypeName.value}
                      onChange={handleInputChange}
                      onBlur={(e) =>
                        handleOnBlur(e.target.name, e.target.value)
                      }
                      required
                      maxLength={100}
                    />
                    {formData.jobTypeName.error && (
                      <small className="validation-error">
                        {formData.jobTypeName.error}
                      </small>
                    )}
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label>
                      Status <span className="validation-error">*</span>
                    </Label>
                    <div className="radio-rapper">
                      <FormGroup check inline>
                        <Label check>
                          <Input
                            type="radio"
                            name="status"
                            value="ACTIVE"
                            checked={formData.status.value === "ACTIVE"}
                            onChange={handleInputChange}
                          />{" "}
                          Active
                        </Label>
                      </FormGroup>
                      <FormGroup check inline>
                        <Label check>
                          <Input
                            type="radio"
                            name="status"
                            value="INACTIVE"
                            checked={formData.status.value === "INACTIVE"}
                            onChange={handleInputChange}
                          />{" "}
                          Inactive
                        </Label>
                      </FormGroup>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>

            <CardFooter className="d-flex justify-content-end">
              <Button
                color="secondary"
                onClick={() => toggleModal()}
                className="mr-2"
                disabled={showLoader}
              >
                Cancel
              </Button>
              <Button
                color="default"
                type="submit"
                onClick={
                  modalState.mode === "add" ? handleAddSubmit : handleEditSubmit
                }
                disabled={Object.values(formData).some(
                  (item) => item.error !== "",
                )}
              >
                {showLoader ? (
                  <i className="fa fa-spinner fa-spin fa-lg text-white" />
                ) : (
                  "Save"
                )}
              </Button>
            </CardFooter>
          </Card>
        </RightPanelCard>
      </Container>
    </>
  );
};

export default JobTypeMaster;
